import React, { useContext, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Breakpoint } from "@mui/material";
import { COACH_PROGRAMS_ROUTE } from "../../../routes/routes";
import CommonSuspenseWrapper from "../client/CommonSuspenseWrapper";
import EnrollmentsClientService from "../../../services/EnrollmentsClientService";
import { useQuery } from "@tanstack/react-query";
import { ProgramContext } from "../../../contexts/ProgramContext";
import { CurriculumErrorAppDialog } from "../../../components/dialog/app/CurriculumErrorAppDialog";
import { useCurriculumSelector } from "../../../redux/hooks";
import {
  selectApiErrorState,
  selectLatestDebugWarnings,
} from "../../../redux/api/selectors";
import { extractSlugId } from "../../../utils/slug";
import { useToastAlert } from "../../../components/app/ToastAlert/ToastAlertProvider";
import DebugWarningsList from "../../../components/curriculum/DebugWarningsList";

export const COACH_PROGRAMS_WRAPPER_QUERY_KEY = "coach-programs-wrapper";

const ErrorHandler = () => {
  const curriculumHasError = useCurriculumSelector(selectApiErrorState);
  const debugWarnings = useCurriculumSelector(selectLatestDebugWarnings);
  const { showToastAlert } = useToastAlert();

  React.useEffect(() => {
    // Debug warnings should appear in state only when the cookie is set
    if (debugWarnings && debugWarnings.length > 0) {
      showToastAlert("error", {
        message: <DebugWarningsList warnings={debugWarnings} />,
      });
    }
  }, [debugWarnings]);

  return curriculumHasError ? (
    <CurriculumErrorAppDialog open={curriculumHasError} />
  ) : null;
};

const CoachProgramsWrapper = () => {
  const location = useLocation();
  const { slug } = useParams();
  const slugId = extractSlugId(slug);
  const avatarsToTake = 4;

  const {
    data: program,
    isLoading: programLoading,
    error,
  } = useQuery({
    queryKey: [COACH_PROGRAMS_WRAPPER_QUERY_KEY, { slugId }],
    queryFn: () =>
      EnrollmentsClientService.getProgramEnrollmentInfo(slug, avatarsToTake),
  });

  if (error) {
    console.error("Error fetching program data:", error);
  }

  const context = useContext(ProgramContext);

  useEffect(() => {
    if (program) {
      context.setProgramQuery(program);
    }

    // reset context on unmount
    return () => {
      context.resetProgramQuery();
    };
  }, [program]);

  const maxWidth: Breakpoint = React.useMemo(() => {
    if (!location.pathname.includes(COACH_PROGRAMS_ROUTE)) return undefined;
    else if (location.pathname.includes("curriculum")) {
      return "slg";
    } else if (location.pathname.includes("enrollments")) {
      return "md";
    } else if (location.pathname.includes("settings")) {
      return "md";
    }
    return undefined;
  }, [location.pathname]);

  return (
    <>
      <ErrorHandler />
      {CommonSuspenseWrapper({ maxWidth })}
    </>
  );
};

export default CoachProgramsWrapper;
